import React from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  AlertTriangle, 
  XCircle, 
  Info,
  Users,
  MessageCircle,
  Gamepad2,
  Car,
  Building
} from 'lucide-react';
import SEO from '../components/SEO';
import ShinyText from '../components/ShinyText';

const Rules: React.FC = () => {
  const rules = [
    {
      id: 'general',
      title: 'Genel Kurallar',
      icon: Shield,
      severity: 'high',
      rules: [
        {
          rule: 'Oyunculara saygılı olun',
          description: 'Tüm oyunculara karşı saygılı davranın. Hakaret, küfür ve tehdit kesinlikle yasaktır.',
          punishment: 'Uyarı / Geçici Ban'
        },
        {
          rule: 'Hile kullanımı yasaktır',
          description: 'Herhangi bir hile programı kullanmak kesinlikle yasaktır. Tespit edilmesi durumunda kalıcı ban uygulanır.',
          punishment: 'Kalıcı Ban'
        },
        {
          rule: 'Spam ve flood yapmayın',
          description: 'Chat\'te spam yapmak, aynı mesajı tekrar tekrar göndermek yasaktır.',
          punishment: 'Mute / Geçici Ban'
        },
        {
          rule: 'Reklam yapmayın',
          description: 'Başka sunucuların reklamını yapmak yasaktır.',
          punishment: 'Geçici Ban'
        }
      ]
    },
    {
      id: 'roleplay',
      title: 'Roleplay Kuralları',
      icon: Users,
      severity: 'high',
      rules: [
        {
          rule: 'Karakterinizi tutarlı oynayın',
          description: 'Karakterinizin geçmişi ve kişiliği ile tutarlı davranın. Ani karakter değişiklikleri yapmayın.',
          punishment: 'Uyarı / Kick'
        },
        {
          rule: 'Metagaming yapmayın',
          description: 'Oyun dışında öğrendiğiniz bilgileri oyun içinde kullanmayın.',
          punishment: 'Kick / Geçici Ban'
        },
        {
          rule: 'Powergaming yapmayın',
          description: 'Gerçekçi olmayan, aşırı güçlü karakterler oluşturmayın.',
          punishment: 'Uyarı / Kick'
        },
        {
          rule: 'Fail RP yapmayın',
          description: 'Roleplay kurallarına uygun olmayan davranışlar sergilemeyin.',
          punishment: 'Kick / Geçici Ban'
        }
      ]
    },
    {
      id: 'communication',
      title: 'İletişim Kuralları',
      icon: MessageCircle,
      severity: 'medium',
      rules: [
        {
          rule: 'Sadece Türkçe konuşun',
          description: 'Sunucu içinde sadece Türkçe konuşun. Yabancı dil kullanımı yasaktır.',
          punishment: 'Uyarı / Mute'
        },
        {
          rule: 'Ses kalitesi iyi olmalı',
          description: 'Mikrofon kalitesi kötü olan oyuncular ses kanallarına alınmayabilir.',
          punishment: 'Ses kanalından çıkarılma'
        },
        {
          rule: 'Gereksiz ses yapmayın',
          description: 'Arka plan gürültüsü, müzik çalma gibi rahatsız edici sesler yasaktır.',
          punishment: 'Mute / Kick'
        },
        {
          rule: 'Ses kanallarında saygılı olun',
          description: 'Ses kanallarında da yazılı chat kuralları geçerlidir.',
          punishment: 'Mute / Geçici Ban'
        }
      ]
    },
    {
      id: 'vehicles',
      title: 'Araç Kuralları',
      icon: Car,
      severity: 'medium',
      rules: [
        {
          rule: 'Trafik kurallarına uyun',
          description: 'Şehir içinde trafik kurallarına uyun. Aşırı hız ve kaza yapmak yasaktır.',
          punishment: 'Uyarı / Para Cezası'
        },
        {
          rule: 'Araç spawn spam yapmayın',
          description: 'Sürekli araç spawn etmek ve araçları terk etmek yasaktır.',
          punishment: 'Kalıcı Ban'
        },
        {
          rule: 'Araç modifikasyonları gerçekçi olmalı',
          description: 'Araç modifikasyonları gerçekçi sınırlar içinde olmalıdır.',
          punishment: 'Uyarı'
        }
      ]
    },
    {
      id: 'properties',
      title: 'Mülk ve İş Kuralları',
      icon: Building,
      severity: 'medium',
      rules: [
        {
          rule: 'Mülk satışı kurallara uygun olmalı',
          description: 'Mülk satışları gerçekçi fiyatlarla yapılmalıdır.',
          punishment: 'Uyarı / Mülk elinden alma'
        },
        {
          rule: 'İş yerlerinde roleplay yapın',
          description: 'İş yerlerinde çalışırken roleplay kurallarına uyun.',
          punishment: 'Uyarı / İşten çıkarılma'
        },
        {
          rule: 'Mülk işgalleri yasaktır',
          description: 'Başkalarının mülklerini işgal etmek yasaktır.',
          punishment: 'Kick / Geçici Ban'
        }
      ]
    },
    {
      id: 'prohibited',
      title: 'Yasaklı Davranışlar',
      icon: XCircle,
      severity: 'high',
      rules: [
        {
          rule: 'Discriminasyon yasaktır',
          description: 'Irk, din, cinsiyet ayrımcılığı kesinlikle yasaktır.',
          punishment: 'Kalıcı Ban'
        },
        {
          rule: 'Cinsel içerik yasaktır',
          description: 'Cinsel içerikli konuşmalar ve davranışlar yasaktır.',
          punishment: 'Geçici Ban / Kalıcı Ban'
        },
        {
          rule: 'Kişisel bilgi paylaşımı yasaktır',
          description: 'Başkalarının kişisel bilgilerini paylaşmak yasaktır.',
          punishment: 'Geçici Ban'
        },
        {
          rule: 'Sunucuya zarar vermek yasaktır',
          description: 'Sunucuya kasıtlı olarak zarar vermek yasaktır.',
          punishment: 'Kalıcı Ban'
        }
      ]
    }
  ];

  const punishments = [
    {
      type: 'Uyarı',
      description: 'İlk ihlal durumunda verilen uyarı',
      color: 'text-yellow-400'
    },
    {
      type: 'Mute',
      description: 'Chat ve ses kanallarından geçici susturma',
      color: 'text-orange-400'
    },
    {
      type: 'Kick',
      description: 'Sunucudan geçici olarak çıkarılma',
      color: 'text-red-400'
    },
    {
      type: 'Geçici Ban',
      description: 'Belirli bir süre sunucuya giriş yapamama',
      color: 'text-red-500'
    },
    {
      type: 'Kalıcı Ban',
      description: 'Sunucudan kalıcı olarak uzaklaştırılma',
      color: 'text-red-600'
    }
  ];

  return (
    <>
      <SEO
        title="Sunucu Kuralları - Aedon Community"
        description="Sunucumuzda uyulması gereken kurallar ve cezalar. Genel kurallar, roleplay kuralları, iletişim kuralları ve daha fazlası."
        keywords="aedon community, sunucu kuralları, fiveM kuralları, roleplay kuralları, iletişim kuralları, sunucu cezaları, FiveM kurallar"
        url="/rules"
      />
      <div className="min-h-screen pt-20 bg-dark-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-gaming font-bold text-white mb-4">
            Sunucu Kuralları
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Sunucumuzda uyulması gereken kurallar ve cezalar
          </p>
        </motion.div>

        {/* Warning Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 mb-12"
        >
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Önemli Uyarı</h3>
              <p className="text-gray-300">
                Bu kurallar sunucumuzda oyun oynamak için zorunludur. Kuralları ihlal eden oyuncular 
                ceza alabilir. Kuralları okumamış olmak ceza almamak için mazeret değildir.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Punishment Guide */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-gaming font-bold text-white mb-6">Cezalar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {punishments.map((punishment, index) => (
              <div key={index} className="card">
                <h3 className="text-lg font-semibold mb-2">
                  <ShinyText 
                    text={punishment.type} 
                    speed={4} 
                    variant={
                      punishment.type === 'Uyarı' ? 'yellow' :
                      punishment.type === 'Mute' ? 'orange' :
                      punishment.type === 'Kick' ? 'red' :
                      punishment.type === 'Geçici Ban' ? 'red' :
                      punishment.type === 'Kalıcı Ban' ? 'red' : 'blue'
                    }
                    className="text-lg font-semibold"
                  />
                </h3>
                <p className="text-gray-400 text-sm">{punishment.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Rules Sections */}
        <div className="space-y-8">
          {rules.map((section, index) => {
            const Icon = section.icon;
            
            return (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card"
              >
                <div className="flex items-center space-x-3 mb-6">
                  <Icon className="w-8 h-8 text-primary-400" />
                  <div>
                    <h2 className="text-2xl font-gaming font-bold text-white">{section.title}</h2>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-xs px-2 py-1 rounded ${
                        section.severity === 'high' 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {section.severity === 'high' ? 'Kritik' : 'Önemli'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  {section.rules.map((rule, ruleIndex) => (
                    <div key={ruleIndex} className="border-l-2 border-primary-500 pl-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-semibold text-white">{rule.rule}</h3>
                        <span className="text-sm bg-dark-600 px-2 py-1 rounded">
                          <ShinyText 
                            text={rule.punishment} 
                            speed={4} 
                            variant={
                              rule.punishment.includes('Uyarı') ? 'yellow' :
                              rule.punishment.includes('Mute') ? 'orange' :
                              rule.punishment.includes('Kick') ? 'red' :
                              rule.punishment.includes('Kalıcı Ban') ? 'red' :
                              rule.punishment.includes('Geçici Ban') ? 'red' : 'black-white'
                            }
                            className="text-xs font-medium"
                          />
                        </span>
                      </div>
                      <p className="text-gray-400 mb-3">{rule.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Appeal Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 card text-center"
        >
          <Info className="w-12 h-12 text-primary-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Cezanıza İtiraz Etmek İstiyorsanız</h3>
          <p className="text-gray-400 mb-6">
            Cezanıza itiraz etmek için Discord sunucumuzdaki #itiraz kanalını kullanabilirsiniz. 
            İtirazınızı yaparken lütfen detaylı açıklama yapın.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://discord.gg/aedonv" target="_blank" rel="noopener noreferrer" className="btn-primary">
              <MessageCircle className="w-4 h-4 mr-2" />
              Discord'a Katıl
            </a>
            <a href="/guide" className="btn-secondary">
              <Gamepad2 className="w-4 h-4 mr-2" />
              Rehberi Oku
            </a>
          </div>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default Rules; 